import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { initPassengerMessaging } from '../services/notifications';
import { isNativeApp } from '../utils/platform';
import { openAppSettings } from '../utils/locationPermission';
import useScrollLock from '../utils/useScrollLock';

const OK_KEY = 'mt_rides_push_ok';
const SKIP_AT_KEY = 'mt_rides_push_sheet_skip_at';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

const recentlySkipped = () => {
  try {
    const at = Number(localStorage.getItem(SKIP_AT_KEY) || 0);
    return at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
};

const checkOsPermission = async () => {
  if (isNativeApp()) {
    try {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const perm = await PushNotifications.checkPermissions();
      return perm.receive === 'granted';
    } catch {
      return false;
    }
  }
  if (typeof Notification === 'undefined') return false;
  return Notification.permission === 'granted';
};

/**
 * Once Android has genuinely denied notification permission, this app (like
 * any other) can never re-trigger that system dialog — Settings is the only
 * way back. Mirrors locationPermission.js's showLocationDeniedToast exactly
 * (same AppSettings plugin, same visual style) rather than a second copy of
 * that mechanism.
 */
const showNotificationDeniedToast = (t) => {
  const message = t('push.denied');
  if (!isNativeApp()) {
    toast.error(message, { id: 'push-status' });
    return;
  }
  toast.error(
    React.createElement(
      'span',
      { style: { display: 'flex', flexDirection: 'column', gap: 8 } },
      message,
      React.createElement(
        'button',
        {
          type: 'button',
          onClick: () => {
            toast.dismiss();
            openAppSettings();
          },
          style: {
            alignSelf: 'flex-start',
            padding: '6px 14px',
            borderRadius: 10,
            border: 'none',
            background: 'var(--green-dark, #007a4d)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 13,
          },
        },
        t('book.openSettings'),
      ),
    ),
    { id: 'push-status', duration: 9000 },
  );
};

/**
 * MyTownly-branded bottom sheet for ride alerts.
 * Reappears when OS notification permission is off (after snooze).
 * @param {boolean} blocked — hide while tour (etc.) owns the screen
 * @param {(active: boolean) => void} onActiveChange — true while this sheet is visible
 * @param {() => void} onReady — first permission check finished (so Book can show next sheet)
 */
export default function NotificationPrompt({ uid, t, blocked = false, onActiveChange, onReady }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const onReadyRef = useRef(onReady);
  const onActiveRef = useRef(onActiveChange);
  onReadyRef.current = onReady;
  onActiveRef.current = onActiveChange;

  useEffect(() => {
    if (!uid) {
      onReadyRef.current?.();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        if (recentlySkipped()) {
          setOpen(false);
          return;
        }
        const granted = await checkOsPermission();
        if (cancelled) return;
        if (granted) {
          try { localStorage.setItem(OK_KEY, '1'); } catch { /* */ }
          setOpen(false);
          return;
        }
        try { localStorage.removeItem(OK_KEY); } catch { /* */ }
        setOpen(true);
      } finally {
        if (!cancelled) onReadyRef.current?.();
      }
    })();

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      checkOsPermission().then((granted) => {
        if (cancelled) return;
        if (granted) {
          try { localStorage.setItem(OK_KEY, '1'); } catch { /* */ }
          setOpen(false);
        } else if (!recentlySkipped()) {
          try { localStorage.removeItem(OK_KEY); } catch { /* */ }
          setOpen(true);
        }
      });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uid]);

  const visible = Boolean(open && uid && !blocked);
  useScrollLock(visible);

  useEffect(() => {
    onActiveRef.current?.(visible);
    return () => onActiveRef.current?.(false);
  }, [visible]);

  if (!visible) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const token = await initPassengerMessaging(uid, { request: true });
      const granted = await checkOsPermission();
      if (token || granted) {
        try {
          localStorage.setItem(OK_KEY, '1');
          localStorage.removeItem(SKIP_AT_KEY);
        } catch { /* */ }
        setOpen(false);
        toast.success(t('push.enabled'), { id: 'push-status' });
      } else {
        showNotificationDeniedToast(t);
      }
    } catch {
      showNotificationDeniedToast(t);
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    try { localStorage.setItem(SKIP_AT_KEY, String(Date.now())); } catch { /* */ }
    setOpen(false);
  };

  return (
    <div className="notif-sheet-overlay" role="presentation" onClick={skip}>
      <div
        className="notif-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notif-sheet-title"
        data-tour="push"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="notif-sheet-handle" aria-hidden />
        <div className="notif-sheet-hero">
          <div className="notif-sheet-bell" aria-hidden>🔔</div>
          <h2 id="notif-sheet-title">{t('push.sheetTitle')}</h2>
        </div>
        <ul className="notif-sheet-list">
          <li>
            <span className="notif-sheet-ico" aria-hidden>🛺</span>
            <div>
              <strong>{t('push.sheetRideTitle')}</strong>
              <p>{t('push.sheetRideBody')}</p>
            </div>
          </li>
          <li>
            <span className="notif-sheet-ico" aria-hidden>📍</span>
            <div>
              <strong>{t('push.sheetArriveTitle')}</strong>
              <p>{t('push.sheetArriveBody')}</p>
            </div>
          </li>
        </ul>
        <button type="button" className="btn btn-primary notif-sheet-allow" disabled={busy} onClick={enable}>
          {t('push.sheetAllow')}
        </button>
        <button type="button" className="btn btn-outline notif-sheet-later" disabled={busy} onClick={skip}>
          {t('push.sheetLater')}
        </button>
      </div>
    </div>
  );
}
